/**
 * Secondary, INDEPENDENT signature backup.
 *
 * Why this file exists
 * --------------------
 * Per the in-home assessment lifecycle, signatures must be captured 100% on the
 * first visit — the RN does not return to the client's home for another 60 days.
 * If a signature is ever lost (browser crash, accidental wizard reset, user
 * clears localStorage, draft expires), the entire visit has to be re-done.
 *
 * The wizard state is auto-saved as one large JSON blob keyed by `wi-pa-wizard-v1`.
 * That blob is wiped on `reset()`, on "Discard draft", and on form-submission
 * cleanup. We therefore keep a parallel, ALWAYS-APPENDED backup of just the
 * signature PNGs, keyed by their own namespace, that survives wizard resets.
 *
 * On wizard mount, if the wizard state has empty signatures but the backup
 * has them, we restore from the backup so a typed-name reset never wipes the
 * RN's hard-won finger captures.
 */

const BACKUP_KEY = "wi-pa-signatures-backup-v1";

function readBackup() {
  try {
    const raw = window.localStorage.getItem(BACKUP_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeBackup(obj) {
  try {
    window.localStorage.setItem(BACKUP_KEY, JSON.stringify(obj));
    return true;
  } catch {
    return false;
  }
}

/** Save a single signature PNG (data URL) under its role. */
export function saveSignatureBackup(role, dataUrl) {
  if (!role || !dataUrl) return false;
  const all = readBackup();
  all[role] = {
    data: dataUrl,
    saved_at: new Date().toISOString(),
  };
  return writeBackup(all);
}

/** Read a single backup PNG (returns the data URL or null). */
export function loadSignatureBackup(role) {
  const all = readBackup();
  return all[role]?.data || null;
}

/** When was this signature backup last written? Returns ISO string or null. */
export function signatureBackupTimestamp(role) {
  const all = readBackup();
  return all[role]?.saved_at || null;
}

/** Read all role -> {data, saved_at} entries. */
export function listSignatureBackups() {
  return readBackup();
}

/** Remove a single role's backup (used on a successful submission). */
export function clearSignatureBackup(role) {
  const all = readBackup();
  if (role in all) {
    delete all[role];
    writeBackup(all);
  }
}

/** Wipe ALL signature backups. Only call after a confirmed successful PDF submission. */
export function clearAllSignatureBackups() {
  try {
    window.localStorage.removeItem(BACKUP_KEY);
  } catch {
    /* non-fatal */
  }
}

export const SIGNATURE_BACKUP_KEY = BACKUP_KEY;
